import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Calendar, Dumbbell, Users, Images, LogOut, User, Bell, TrendingUp, Newspaper, Award, Menu, X, Target, Music, ChevronDown, Gamepad2, MessageSquare, ShoppingCart, Megaphone, CloudSun, Leaf, Palette, Trophy, Globe, Mic, Drama, Radio } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [miscMenuOpen, setMiscMenuOpen] = useState(false);
  const [mundoMKMenuOpen, setMundoMKMenuOpen] = useState(false);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread/count`);
      const count = response.data.count;
      setUnreadCount(count);
      
      // Actualizar badge en el icono de la app (PWA)
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          navigator.setAppBadge(count);
        } else {
          navigator.clearAppBadge();
        }
      }
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Polling cada 60 segundos para reducir carga en el servidor
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    // Cerrar menú móvil al cambiar de ruta
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const NavLink = ({ to, icon: Icon, label, testId, showBadge, badgeCount }) => (
    <Link to={to} data-testid={testId} onClick={() => setMobileMenuOpen(false)}>
      <Button
        variant={isActive(to) ? 'default' : 'ghost'}
        className={`${isActive(to) ? 'btn-primary-mk' : 'text-white hover:text-gold'} w-full justify-start md:w-auto md:justify-center px-3 py-2`}
        size="sm"
      >
        <div className="relative">
          <Icon className="h-4 w-4 md:mr-2" />
          {showBadge && badgeCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
              {badgeCount}
            </span>
          )}
        </div>
        <span className="ml-2 md:hidden lg:inline">{label}</span>
      </Button>
    </Link>
  );

  return (
    <nav className="navbar-mk sticky top-0 z-50 shadow-lg" data-testid="navbar">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_espanol-mode-15/artifacts/jlomgnz6_WhatsApp%20Image%202025-11-16%20at%2010.07.15.jpeg"
              alt="Mallorca Klan Logo"
              className="h-8 md:h-10 w-auto"
            />
            <div className="hidden sm:flex flex-col">
              <span className="text-gold font-bold text-base md:text-xl leading-tight">
                Mallorca Klan
              </span>
              <span className="text-gray-400 text-xs italic hidden lg:block">
                Aquí empieza el cambio
              </span>
            </div>
          </Link>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-white hover:text-gold"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <NavLink to="/" icon={Calendar} label="Calendario" testId="nav-dashboard" />
            
            {user?.role === 'entrenador' && (
              <>
                <NavLink to="/admin/trainings" icon={Calendar} label="Admin" testId="nav-admin-trainings" />
                <NavLink to="/entrenos" icon={Dumbbell} label="Entrenos" testId="nav-entrenos" />
                <NavLink to="/usuarios" icon={Users} label="Usuarios" testId="nav-usuarios" />
                <NavLink to="/estadisticas" icon={TrendingUp} label="Stats" testId="nav-estadisticas" />
              </>
            )}

            <NavLink to="/galeria" icon={Images} label="Galería" testId="nav-galeria" />
            <NavLink to="/noticias" icon={Newspaper} label="Noticias" testId="nav-noticias" />
            <NavLink to="/sponsors" icon={Award} label="Sponsors" testId="nav-sponsors" />
            
            {/* Dropdown Miscelánea (Juegos) */}
            <div className="relative">
              <Button
                variant="ghost"
                className={`text-white hover:text-gold px-3 py-2 flex items-center gap-1 ${
                  ['/100-tiros', '/mk-titan', '/words', '/impostor', '/cuadros'].includes(location.pathname) ? 'text-gold' : ''
                }`}
                size="sm"
                onClick={() => { setMiscMenuOpen(!miscMenuOpen); setMundoMKMenuOpen(false); }}
                onBlur={() => setTimeout(() => setMiscMenuOpen(false), 150)}
              >
                <Gamepad2 className="h-4 w-4" />
                <span className="hidden lg:inline ml-1">Miscelánea</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${miscMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              {miscMenuOpen && (
                <div className="absolute top-full right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[160px] z-50">
                  <Link 
                    to="/100-tiros" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMiscMenuOpen(false)}
                  >
                    <Target className="h-4 w-4" />
                    100 Tiros
                  </Link>
                  <Link 
                    to="/mk-titan" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMiscMenuOpen(false)}
                  >
                    <Music className="h-4 w-4" />
                    MK Titan
                  </Link>
                  <Link 
                    to="/words" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMiscMenuOpen(false)}
                  >
                    <MessageSquare className="h-4 w-4" />
                    Words
                  </Link>
                  <Link 
                    to="/impostor" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMiscMenuOpen(false)}
                  >
                    <Drama className="h-4 w-4" />
                    Impostor
                  </Link>
                  {(user?.es_pro || user?.role === 'entrenador') && (
                    <Link 
                      to="/mk-voice" 
                      className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                      onClick={() => setMiscMenuOpen(false)}
                    >
                      <Mic className="h-4 w-4" />
                      MK Voice
                    </Link>
                  )}
                  <Link 
                    to="/mk-live" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMiscMenuOpen(false)}
                  >
                    <Radio className="h-4 w-4" />
                    MK Live
                  </Link>
                  {user?.role === 'entrenador' && (
                    <Link 
                      to="/cuadros" 
                      className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold border-t border-gray-700"
                      onClick={() => setMiscMenuOpen(false)}
                    >
                      <Palette className="h-4 w-4" />
                      Portfolio
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Dropdown Mundo MK */}
            <div className="relative">
              <Button
                variant="ghost"
                className={`text-white hover:text-gold px-3 py-2 flex items-center gap-1 ${
                  ['/tiempo', '/pedidos', '/mkdietal', '/mkdietal/admin', '/hall-of-fame', '/admin/anuncios'].includes(location.pathname) ? 'text-gold' : ''
                }`}
                size="sm"
                onClick={() => { setMundoMKMenuOpen(!mundoMKMenuOpen); setMiscMenuOpen(false); }}
                onBlur={() => setTimeout(() => setMundoMKMenuOpen(false), 150)}
              >
                <Globe className="h-4 w-4" />
                <span className="hidden lg:inline ml-1">Mundo MK</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${mundoMKMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              {mundoMKMenuOpen && (
                <div className="absolute top-full right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[160px] z-50">
                  <Link 
                    to="/tiempo" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMundoMKMenuOpen(false)}
                  >
                    <CloudSun className="h-4 w-4" />
                    El Tiempo
                  </Link>
                  <Link 
                    to="/pedidos" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMundoMKMenuOpen(false)}
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Pedidos Prozis
                  </Link>
                  <Link 
                    to="/mkdietal" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMundoMKMenuOpen(false)}
                  >
                    <Leaf className="h-4 w-4" />
                    MKdietal
                  </Link>
                  {user?.role === 'entrenador' && (
                    <Link 
                      to="/mkdietal/admin" 
                      className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                      onClick={() => setMundoMKMenuOpen(false)}
                    >
                      <Users className="h-4 w-4" />
                      MKdietal Equipo
                    </Link>
                  )}
                  <Link 
                    to="/hall-of-fame" 
                    className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold"
                    onClick={() => setMundoMKMenuOpen(false)}
                  >
                    <Trophy className="h-4 w-4" />
                    Hall of Fame
                  </Link>
                  {user?.role === 'entrenador' && (
                    <Link 
                      to="/admin/anuncios" 
                      className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 hover:text-gold border-t border-gray-700"
                      onClick={() => setMundoMKMenuOpen(false)}
                    >
                      <Megaphone className="h-4 w-4" />
                      Gestión Anuncios
                    </Link>
                  )}
                </div>
              )}
            </div>
            
            <NavLink 
              to="/notificaciones" 
              icon={Bell} 
              label="" 
              testId="nav-notificaciones"
              showBadge={true}
              badgeCount={unreadCount}
            />

            {/* Perfil con indicador Pro */}
            <div className="relative">
              <NavLink to="/perfil" icon={User} label="" testId="nav-perfil" />
              {(user?.es_pro || user?.role === 'entrenador') && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full pro-badge-glow" title="Cliente Pro" />
              )}
            </div>

            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white hover:text-red-500 px-2"
              size="sm"
              data-testid="nav-logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-700 animate-in slide-in-from-top duration-200 max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="flex flex-col gap-2 pb-4">
              <NavLink to="/" icon={Calendar} label="Calendario" testId="nav-dashboard-mobile" />
              
              {user?.role === 'entrenador' && (
                <>
                  <NavLink to="/admin/trainings" icon={Calendar} label="Administrar Entrenos" testId="nav-admin-mobile" />
                  <NavLink to="/entrenos" icon={Dumbbell} label="Plantillas de Entrenos" testId="nav-entrenos-mobile" />
                  <NavLink to="/usuarios" icon={Users} label="Usuarios" testId="nav-usuarios-mobile" />
                  <NavLink to="/estadisticas" icon={TrendingUp} label="Estadísticas" testId="nav-stats-mobile" />
                </>
              )}

              <NavLink to="/galeria" icon={Images} label="Galería" testId="nav-galeria-mobile" />
              <NavLink to="/noticias" icon={Newspaper} label="Noticias" testId="nav-noticias-mobile" />
              <NavLink to="/sponsors" icon={Award} label="Sponsors" testId="nav-sponsors-mobile" />
              
              {/* Sección Miscelánea en móvil (Juegos) */}
              <div className="border-t border-gray-700 pt-2 mt-2">
                <p className="text-gray-500 text-xs uppercase px-3 mb-2 flex items-center gap-2">
                  <Gamepad2 className="h-3 w-3" /> Miscelánea
                </p>
                <NavLink to="/mk-live" icon={Radio} label="MK Live" testId="nav-mklive-mobile" />
                <NavLink to="/100-tiros" icon={Target} label="100 Tiros" testId="nav-100tiros-mobile" />
                <NavLink to="/mk-titan" icon={Music} label="MK Titan" testId="nav-mk-titan-mobile" />
                <NavLink to="/words" icon={MessageSquare} label="Words" testId="nav-words-mobile" />
                <NavLink to="/impostor" icon={Drama} label="Impostor" testId="nav-impostor-mobile" />
                {(user?.es_pro || user?.role === 'entrenador') && (
                  <NavLink to="/mk-voice" icon={Mic} label="MK Voice" testId="nav-mkvoice-mobile" />
                )}
                {user?.role === 'entrenador' && (
                  <NavLink to="/cuadros" icon={Palette} label="Portfolio" testId="nav-cuadros-mobile" />
                )}
              </div>

              {/* Sección Mundo MK en móvil */}
              <div className="border-t border-gray-700 pt-2 mt-2">
                <p className="text-gray-500 text-xs uppercase px-3 mb-2 flex items-center gap-2">
                  <Globe className="h-3 w-3" /> Mundo MK
                </p>
                <NavLink to="/tiempo" icon={CloudSun} label="El Tiempo" testId="nav-tiempo-mobile" />
                <NavLink to="/pedidos" icon={ShoppingCart} label="Pedidos Prozis" testId="nav-pedidos-mobile" />
                <NavLink to="/mkdietal" icon={Leaf} label="MKdietal" testId="nav-mkdietal-mobile" />
                {user?.role === 'entrenador' && (
                  <NavLink to="/mkdietal/admin" icon={Users} label="MKdietal Equipo" testId="nav-mkdietal-admin-mobile" />
                )}
                <NavLink to="/hall-of-fame" icon={Trophy} label="Hall of Fame" testId="nav-hall-of-fame-mobile" />
                {user?.role === 'entrenador' && (
                  <NavLink to="/admin/anuncios" icon={Megaphone} label="Gestión Anuncios" testId="nav-anuncios-mobile" />
                )}
              </div>
              
              <div className="border-t border-gray-700 pt-2 mt-2">
                <NavLink 
                  to="/notificaciones" 
                  icon={Bell} 
                  label={`Notificaciones ${unreadCount > 0 ? `(${unreadCount})` : ''}`}
                  testId="nav-notificaciones-mobile"
                />

                <NavLink to="/perfil" icon={User} label="Mi Perfil" testId="nav-perfil-mobile" />

                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white hover:text-red-500 w-full justify-start px-3 py-2"
                  size="sm"
                  data-testid="nav-logout-mobile"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar Sesión
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
